import React from 'react';
import { BillingAndCard } from './BillingAndCard';
import { BillingContacts, RewardsContact } from './BillingContacts';
import { InvoiceHistory } from './InvoiceHistory';
import {
  BillingCycle,
  ProductType,
  TeamForAdminFragment,
  useAvailableAddOnsQuery,
  useMyTeamForAdminQuery,
  useSubscriptionInfoQuery,
} from '$/GraphQL/schema/schema';
import Loading from '$/components/Loading';
import { StripeLoader } from '$/components/StripeLoader';
import { css } from '@emotion/react';
import { SubscriptionInfoCards } from './SubscriptionInfoCards';
import { getAddOn } from './AddOns/AddOns';
import { AddOn } from './AddOns/Info/AddOn';
import { AvailableAddOns } from './AddOns/AvailableAddOns';
import tw from 'twin.macro';
import { SuspendedAlert } from '$/components/SuspendedAlert';
import { PlanPageCTAButtonsProvider } from './PlanPageCTAButtons';
import { useMe } from '$/hooks/useMe';
import { SubscriptionInfo } from './SubscriptionInfo';
import { AddOnProvider } from './AddOns/AddOnsContext';
import { CurrentPlan } from './CurrentPlan';
import Settings from '$/components/ui/Settings';
import { UpdateAddOnModal } from './AddOns/UpdateAddOnModal';

interface PlanPageContextValue {
  team: TeamForAdminFragment;
  subscriptionInfo: SubscriptionInfo;
  availableAddOns: AddOn[];
  billingCycle: BillingCycle;
  subscriptionChanges: Partial<Record<ProductType, number>>;
  onSubscriptionChange: (productType: ProductType, quantity: number) => void;
  setBillingCycle: (billingCycle: BillingCycle) => void;
}

const PlanPageContext = React.createContext<PlanPageContextValue>({} as PlanPageContextValue);

export const usePlanPageContext = (): PlanPageContextValue => React.useContext(PlanPageContext);

const PlanPage: React.FC = () => {
  const me = useMe();
  const [subscriptionChanges, setSubscriptionChanges] = React.useState<
    PlanPageContextValue['subscriptionChanges']
  >({});
  const teamQuery = useMyTeamForAdminQuery({
    fetchPolicy: 'network-only',
  });
  const subscriptionInfoQuery = useSubscriptionInfoQuery({
    fetchPolicy: 'network-only',
  });
  const availableAddOnsQuery = useAvailableAddOnsQuery({
    fetchPolicy: 'network-only',
  });

  const team = teamQuery.data?.myTeam;

  const subscriptionInfo = React.useMemo(() => {
    if (!subscriptionInfoQuery.data?.subscriptionInfo) return undefined;
    return new SubscriptionInfo(subscriptionInfoQuery.data?.subscriptionInfo, me.team.id);
  }, [subscriptionInfoQuery.data?.subscriptionInfo, me.team.id]);

  const [billingCycle, setBillingCycle] = React.useState(
    subscriptionInfo?.billingCycle ?? BillingCycle.Month
  );

  const onSubscriptionChange = React.useCallback(
    (productType: ProductType, quantity: number) => {
      setSubscriptionChanges((prev) => ({ ...prev, [productType]: quantity }));
    },
    [setSubscriptionChanges]
  );

  React.useEffect(() => {
    setBillingCycle(subscriptionInfo?.billingCycle ?? BillingCycle.Month);
  }, [subscriptionInfo]);

  const availableAddOns = React.useMemo(
    () => (availableAddOnsQuery.data?.availableAddOns ?? []).map(getAddOn),
    [availableAddOnsQuery.data?.availableAddOns]
  );

  if (!team || !subscriptionInfo) {
    return <Loading query={teamQuery} />;
  }

  return (
    <PlanPageContext.Provider
      value={{
        team,
        subscriptionInfo,
        availableAddOns,
        billingCycle,
        subscriptionChanges,
        setBillingCycle,
        onSubscriptionChange,
      }}
    >
      <StripeLoader>
        <PlanPageCTAButtonsProvider subscriptionInfo={subscriptionInfo}>
          <SuspendedAlert />

          <Settings.Page
            title='Subscription'
            page='myplan'
            excludeSections={availableAddOns.length === 0 ? ['m-addons'] : []}
            css={css`
              table thead td {
                ${tw`font-semibold`}
              }
            `}
          >
            <SubscriptionInfoCards />

            <AddOnProvider>
              <Settings.Section id='subscriptions'>
                <Settings.Subsection>
                  <CurrentPlan />
                </Settings.Subsection>
              </Settings.Section>

              <AvailableAddOns />
              <UpdateAddOnModal />
            </AddOnProvider>

            <Settings.Section id='card'>
              <Settings.Subsection>
                <BillingAndCard />
              </Settings.Subsection>
            </Settings.Section>

            <Settings.Section id='contacts'>
              <BillingContacts />
            </Settings.Section>

            <RewardsContact />

            <Settings.Section id='invoices'>
              <Settings.Subsection>
                <InvoiceHistory />
              </Settings.Subsection>
            </Settings.Section>
          </Settings.Page>
        </PlanPageCTAButtonsProvider>
      </StripeLoader>
    </PlanPageContext.Provider>
  );
};

export default PlanPage;
